import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  BriefcaseBusiness,
  CalendarClock,
  FileSpreadsheet,
  LayoutDashboard,
  ShieldCheck,
  Users,
  UsersRound,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { CrmClientesTab } from "./crm/CrmClientesTab";
import { CrmOportunidadesTab } from "./crm/CrmOportunidadesTab";
import { CrmAgendaTab } from "./crm/CrmAgendaTab";
import { CrmResumenTab } from "./crm/CrmResumenTab";
import { CrmPolizasTab } from "./crm/CrmPolizasTab";
import { CrmImportacionTab } from "./crm/CrmImportacionTab";
import { CrmEquipoTab } from "./crm/CrmEquipoTab";
type CrmTab =
  | "resumen"
  | "clientes"
  | "oportunidades"
  | "agenda"
  | "polizas"
  | "importacion"
  | "equipo";

const TABS = [
  {
    id: "resumen" as const,
    label: "Resumen",
    icon: LayoutDashboard,
  },
  {
    id: "clientes" as const,
    label: "Empresas",
    icon: Users,
  },
  {
    id: "oportunidades" as const,
    label: "Oportunidades",
    icon: BriefcaseBusiness,
  },
  {
    id: "agenda" as const,
    label: "Agenda",
    icon: CalendarClock,
  },
  {
    id: "polizas" as const,
    label: "Pólizas",
    icon: ShieldCheck,
  },
  {
    id: "importacion" as const,
    label: "Importación inicial",
    icon: FileSpreadsheet,
  },
  {
    id: "equipo" as const,
    label: "Equipo comercial",
    icon: UsersRound,
  },
];

export function CrmPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] =
    useState<CrmTab>("resumen");

  useEffect(() => {
    const requestedTab = searchParams.get("tab");
    if (TABS.some((tab) => tab.id === requestedTab)) {
      setActiveTab(requestedTab as CrmTab);
      return;
    }
    if (searchParams.has("calendar")) setActiveTab("agenda");
  }, [searchParams]);

  const openTab = useCallback(
    (tab: CrmTab, params: Record<string, string> = {}) => {
      const nextParams = new URLSearchParams();
      nextParams.set("tab", tab);
      Object.entries(params).forEach(([key, value]) => {
        if (value) nextParams.set(key, value);
      });
      setSearchParams(nextParams);
      setActiveTab(tab);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [setSearchParams]
  );
  const visibleTabs = TABS.filter((tab) => {
    if (tab.id === "importacion" || tab.id === "equipo") {
      return user?.role === "admin";
    }
    if (tab.id === "polizas") {
      return user?.role === "admin" || user?.role === "director_comercial";
    }
    return true;
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Cartera CRM
        </h1>

        <p className="mt-1 text-sm text-gray-600">
          Gestión centralizada de empresas, oportunidades,
          pólizas y vencimientos.
        </p>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <p className="text-sm text-blue-900">
          El CRM centraliza todos los clientes y pólizas. Las contrataciones de Accidentes circuito se vinculan automáticamente con la sección Clientes.
        </p>
      </div>

      <div className="border-b border-gray-200">
        <nav className="flex flex-wrap gap-2">
          {visibleTabs.map(
            ({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => openTab(id)}
                className={`flex min-h-11 items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === id
                    ? "border-[#1A3AB8] text-[#1A3AB8]"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
              >
                <Icon className="h-5 w-5" />
                {label}
              </button>
            )
          )}
        </nav>
      </div>

      <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        {activeTab === "resumen" && (
  <CrmResumenTab onOpenTab={openTab} />
)}

        {activeTab === "clientes" && (
  <CrmClientesTab />
)}

       {activeTab === "oportunidades" && (
  <CrmOportunidadesTab />
)}

       {activeTab === "agenda" && (
  <CrmAgendaTab />
)}

       {activeTab === "polizas" && (
  <CrmPolizasTab />
)}

{activeTab === "importacion" && (
  <CrmImportacionTab />
)}

{activeTab === "equipo" && user?.role === "admin" && (
  <CrmEquipoTab />
)}
      </section>
    </div>
  );
}
